<template>
  <BaseSimpleLayout class="role-permission">
    <template #title>ロール権限一覧</template>
    <template #button-area>
      <el-button type="info" :disabled="isLoading" @click="openHistoryDrawer">
        編集履歴
      </el-button>
      <ScreenPermissionGate code="ROLE_PERMISSION_SETTINGS-BTN_CREATE">
        <el-button
          v-if="canCreateRole"
          type="primary"
          :disabled="isLoading"
          @click="openCreateRolePage"
        >
          ロール追加
        </el-button>
      </ScreenPermissionGate>
    </template>

    <template #main>
      <div v-if="canView" v-loading="isLoading">
        <div class="permission-table-note-wrap">
          <div class="permission-table-note">
            ※ 権限設定は画面単位で管理します
          </div>
        </div>
        <el-table
          :data="filteredPermissionRows"
          row-key="id"
          :tree-props="{ children: 'children' }"
          :cell-style="getCellStyle"
          border
          stripe
          :max-height="tableHeight"
          class="permission-table"
        >
          <el-table-column
            prop="name"
            label="機能（画面一覧）"
            fixed
            min-width="320"
          />
          <RolePermissionRoleColumns
            :visible-role-columns="visibleRoleColumns"
            :permission-options="permissionOptions"
            :changed-cell-map="changedCellMap"
            :get-cell-key="getCellKey"
            :can-edit-role-column="canEditRoleColumn"
            :is-editable-screen="isEditableScreen"
            :should-hide-child-access="shouldHideChildAccess"
            :open-edit-role-dialog="openEditRoleDialog"
            :update-permission="updatePermission"
          />
        </el-table>

        <RolePermissionEditHistoryDrawer
          v-model="historyDrawerVisible"
          :items="editHistoryVisible"
          :has-more="hasMoreHistory"
          :loading="historyLoading"
          :loading-more="historyLoadingMore"
          @open="onHistoryDrawerOpen"
          @load-more="loadMoreHistory"
        />
      </div>
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import ScreenPermissionGate from '@/components/ScreenPermissionGate.vue';
  import RolePermissionRoleColumns from '@/pages/master/role-permission/RolePermissionRoleColumns.vue';
  import { buildCellStyle } from '@/pages/master/role-permission/cellStyle';
  import type { EditHistoryItem } from '@/pages/master/role-permission/EditHistoryLog.vue';
  import RolePermissionEditHistoryDrawer from '@/pages/master/role-permission/RolePermissionEditHistoryDrawer.vue';
  import { hasScreenCodePermission } from '@/libs/auth';
  import router from '@/router';
  import store from '@/store';
  import type {
    ActionKey,
    RolePermissionRole,
    RolePermissionScreen
  } from '@/store/modules/rolePermission';

  type PermissionValue = 'full' | 'edit' | 'view' | 'limited' | 'none';
  type RoleKey = string;
  type RoleColumn = {
    key: RoleKey;
    name: string;
    label: string;
  };
  type PermissionRow = {
    id: string;
    name: string;
    kind: 'screen' | 'button';
    screenId?: number;
    permissions: Record<RoleKey, PermissionValue>;
    parentId?: string;
    children?: PermissionRow[];
  };
  type PermissionOption = {
    value: PermissionValue;
    label: string;
  };

  const tableHeight = window.innerHeight - 210;
  const roleColumns = ref<RoleColumn[]>([]);
  const permissionRows = ref<PermissionRow[]>([]);
  const originalPermissionRows = ref<PermissionRow[]>([]);
  const changedCellMap = ref<Record<string, boolean>>({});
  const historyDrawerVisible = ref(false);
  const historyLoading = ref(false);
  const historyLoadingMore = ref(false);
  const editHistory = ref<EditHistoryItem[]>([]);
  const historyNext = ref<number | null>(null);
  const screenNameMap = ref<Record<number, string>>({});
  const managedRoleIdSet = ref(new Set<string>());
  const isLoading = ref(false);
  const editableScreenNames = new Set<string>();

  const permissionOptions: PermissionOption[] = [
    { value: 'full', label: '登録/訂正/削除可' },
    { value: 'edit', label: '訂正可' },
    { value: 'view', label: '閲覧のみ' },
    { value: 'none', label: '権限無し' }
  ];

  const editHistoryVisible = computed(() => editHistory.value);
  const hasMoreHistory = computed(() => historyNext.value !== null);
  const filteredPermissionRows = computed(() => permissionRows.value);
  const visibleRoleColumns = computed(() => roleColumns.value);
  const currentAuthRoleId = computed(() =>
    Number(store.getters['auth/auth'].role ?? 0)
  );
  const currentUserRoleId = ref<string | null>(null);
  const canView = computed(() =>
    hasScreenCodePermission('ROLE_PERMISSION_SETTINGS', 'VIEW')
  );
  // 担当者（roleLevel = 1）はロール追加できない。
  const canCreateRole = computed(
    () => Number(store.getters['auth/auth'].roleLevel ?? 0) !== 1
  );

  const actionKeyToPermission = (actionKey: ActionKey): PermissionValue => {
    if (actionKey === 'CRUD') return 'full';
    if (actionKey === 'EDIT') return 'edit';
    if (actionKey === 'VIEW') return 'view';
    return 'none';
  };
  const permissionToLabel = (value: PermissionValue): string => {
    if (value === 'full') return '登録/訂正/削除可';
    if (value === 'edit') return '訂正可';
    if (value === 'view') return '閲覧のみ';
    return '権限無し';
  };
  const getCellKey = (permissionRowId: string, roleKey: RoleKey) =>
    `${permissionRowId}::${roleKey}`;
  const findPermissionRowById = (
    rows: PermissionRow[],
    id: string
  ): PermissionRow | null => {
    for (const row of rows) {
      if (row.id === id) return row;
      if (row.children?.length) {
        const child = findPermissionRowById(row.children, id);
        if (child) return child;
      }
    }
    return null;
  };
  const shouldHideChildAccess = (row: PermissionRow, roleKey: RoleKey) => {
    if (row.kind !== 'button' || !row.parentId) return false;
    const parentRow = findPermissionRowById(permissionRows.value, row.parentId);
    return parentRow?.permissions[roleKey] === 'none';
  };
  const canEditRoleColumn = (roleKey: RoleKey) =>
    managedRoleIdSet.value.has(roleKey) && roleKey !== currentUserRoleId.value;
  const isEditableScreen = (permissionRowId: string) =>
    editableScreenNames.has(permissionRowId);
  const updatePermission = (
    row: PermissionRow,
    roleKey: RoleKey,
    value: PermissionValue
  ) => {
    row.permissions[roleKey] = value;
    const originalRow = findPermissionRowById(
      originalPermissionRows.value,
      row.id
    );
    changedCellMap.value[getCellKey(row.id, roleKey)] =
      originalRow?.permissions[roleKey] !== value;
  };
  const getCellStyle = buildCellStyle<PermissionRow>((row, column) => {
    const roleKey = column.columnKey as RoleKey | undefined;
    if (!roleKey) return null;
    return {
      highlight: changedCellMap.value[getCellKey(row.id, roleKey)] === true,
      color: '#fff3bf'
    };
  });

  const openHistoryDrawer = () => {
    historyDrawerVisible.value = true;
  };
  const onHistoryDrawerOpen = async () => {
    historyLoading.value = true;
    try {
      await loadHistory();
    } finally {
      historyLoading.value = false;
    }
  };
  const openCreateRolePage = () => {
    router.push('/master/role-permission/create');
  };
  const openEditRoleDialog = (role: RoleColumn) => {
    router.push({
      name: 'RolePermissionDetail',
      params: { roleId: role.key }
    });
  };

  type DiffRow = {
    screenId?: number;
    from?: ActionKey;
    to?: ActionKey;
    oldActionKey?: ActionKey;
    newActionKey?: ActionKey;
  };
  type HistoryApiItem = {
    id: number;
    at: number;
    action: string;
    roleCode?: string;
    roleName?: string;
    editor?: string | null;
    diffPayload?: {
      updatedScreens?: DiffRow[];
      approvedPermissionChanges?: DiffRow[];
      permissionsDraft?: DiffRow[];
      permissionChanges?: DiffRow[];
      comment?: string;
    } | null;
  };
  const mapHistoryItem = (item: HistoryApiItem): EditHistoryItem => {
    const action =
      item.action === 'create' ||
      item.action === 'update' ||
      item.action === 'delete' ||
      item.action === 'approve'
        ? item.action
        : 'update';
    const payload = item.diffPayload ?? null;
    const mapped: EditHistoryItem = {
      id: String(item.id),
      at: Number(item.at),
      action,
      roleCode: item.roleCode,
      roleName: item.roleName,
      editor: item.editor ?? undefined,
      diffPayload: item.diffPayload ?? null
    };

    if (action === 'approve') {
      const updated = Array.isArray(payload?.approvedPermissionChanges)
        ? payload?.approvedPermissionChanges
        : Array.isArray(payload?.updatedScreens)
          ? payload?.updatedScreens
          : [];
      mapped.notes =
        typeof payload?.comment === 'string' ? payload.comment : '';
      mapped.approvedPermissions = updated.map((row: DiffRow) => ({
        screen:
          screenNameMap.value[Number(row.screenId)] ??
          `Screen #${String(row.screenId)}`,
        permission: permissionToLabel(
          actionKeyToPermission(row.newActionKey ?? row.to ?? 'NONE')
        )
      }));
    }

    if (action === 'update') {
      const changes = Array.isArray(payload?.permissionChanges)
        ? payload?.permissionChanges
        : Array.isArray(payload?.permissionsDraft)
          ? payload?.permissionsDraft
          : [];
      mapped.changes = changes.map((row: DiffRow) => ({
        screen:
          screenNameMap.value[Number(row.screenId)] ??
          `Screen #${String(row.screenId)}`,
        oldValue: permissionToLabel(
          actionKeyToPermission(row.from ?? row.oldActionKey ?? 'NONE')
        ),
        newValue: permissionToLabel(
          actionKeyToPermission(row.to ?? row.newActionKey ?? 'NONE')
        )
      }));
    }
    return mapped;
  };
  const loadHistory = async (next?: number) => {
    const response = await store.dispatch('rolePermission/fetchEditHistory', {
      next,
      append: typeof next === 'number'
    });
    const mapped = (response.items ?? []).map(mapHistoryItem);
    editHistory.value =
      typeof next === 'number' ? [...editHistory.value, ...mapped] : mapped;
    historyNext.value = response.next ?? null;
  };
  const loadMoreHistory = async () => {
    if (!hasMoreHistory.value || historyLoadingMore.value) return;
    historyLoadingMore.value = true;
    try {
      await loadHistory(historyNext.value ?? undefined);
    } finally {
      historyLoadingMore.value = false;
    }
  };

  const buildRows = (
    screens: RolePermissionScreen[],
    roles: RolePermissionRole[]
  ): PermissionRow[] => {
    const rolePermissions = roles.reduce(
      (acc, role) => ({
        ...acc,
        [String(role.id)]: role.permissionConfig.reduce(
          (pAcc, p) => ({
            ...pAcc,
            [p.screenId]: actionKeyToPermission(p.actionKey)
          }),
          {} as Record<number, PermissionValue>
        )
      }),
      {} as Record<string, Record<number, PermissionValue>>
    );

    return screens.map((root) => {
      const parentPermissions = roleColumns.value.reduce(
        (acc, role) => ({
          ...acc,
          [role.key]: rolePermissions[role.key]?.[root.id] ?? 'none'
        }),
        {} as Record<string, PermissionValue>
      );
      const children = root.childs.map((child) => {
        const permissions = roleColumns.value.reduce(
          (acc, role) => ({
            ...acc,
            [role.key]: rolePermissions[role.key]?.[child.id] ?? 'none'
          }),
          {} as Record<string, PermissionValue>
        );
        return {
          id: String(child.id),
          name: child.name,
          kind: 'button' as const,
          screenId: child.id,
          parentId: String(root.id),
          permissions
        };
      });

      return {
        id: String(root.id),
        name: root.name,
        kind: 'screen' as const,
        screenId: root.id,
        permissions: parentPermissions,
        children
      };
    });
  };

  onMounted(async () => {
    if (!canView.value) {
      router.push('/top');
      return;
    }
    isLoading.value = true;
    try {
      const [screens, roles] = await Promise.all([
        store.dispatch('rolePermission/fetchScreens'),
        store.dispatch('rolePermission/fetchRoles')
      ]);
      const typedScreens = screens as RolePermissionScreen[];
      const typedRoles = roles as RolePermissionRole[];
      const currentRole = typedRoles.find(
        (role) => role.id === currentAuthRoleId.value
      );
      if (!currentRole) {
        await router.replace('/top');
        return;
      }
      currentUserRoleId.value = currentRole ? String(currentRole.id) : null;
      managedRoleIdSet.value = new Set(
        (currentRole?.managedChildRoleIds ?? []).map((id) => String(id))
      );

      const rolesForColumns = typedRoles.filter((role) => {
        const id = String(role.id);
        return id === currentUserRoleId.value || managedRoleIdSet.value.has(id);
      });

      roleColumns.value = rolesForColumns.map((role) => ({
        key: String(role.id),
        name: role.code,
        label:
          role.permissionConfig.length === 0 ? `(Draf) ${role.name}` : role.name
      }));
      screenNameMap.value = typedScreens.reduce(
        (acc, screen) => {
          acc[screen.id] = screen.name;
          screen.childs.forEach((child) => {
            acc[child.id] = child.name;
          });
          return acc;
        },
        {} as Record<number, string>
      );
      const rows = buildRows(typedScreens, typedRoles);
      permissionRows.value = rows;
      originalPermissionRows.value = JSON.parse(JSON.stringify(rows));
      editableScreenNames.clear();
      rows.forEach((row) => {
        editableScreenNames.add(row.id);
        row.children?.forEach((child) => editableScreenNames.add(child.id));
      });
    } finally {
      isLoading.value = false;
    }
  });
</script>

<style lang="scss">
  .role-permission {
    .header-button-area {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 8px 0;
    }

    .permission-table {
      width: 100%;
    }

    .permission-table-note-wrap {
      display: flex;
      justify-content: flex-end;
      margin: 6px 0 4px;
    }

    .permission-table-note {
      font-size: 12px;
      color: #909399;
      text-align: right;
    }

    .role-header-button {
      border: none;
      background: transparent;
      color: #409eff;
      font-weight: 700;
      cursor: pointer;
      padding: 6px 8px;
      width: 100%;
      text-align: center;
      white-space: normal;
      line-height: 1.3;
      word-break: keep-all;
    }
  }
</style>
